import React, { useState, useEffect } from 'react';
import { Table, Card, Switch, Input, Button, Modal, Timeline, Tooltip, Tag, Space, Form, Typography, Badge, Spin, Row, Col } from 'antd';
import { InfoCircleOutlined, SaveOutlined, HistoryOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPut } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import moment from 'moment';

const { Text, Title } = Typography;

const SecurityToggleSettings = () => {
  const [loading, setLoading] = useState(false);
  const [toggles, setToggles] = useState([]);
  const [filteredToggles, setFilteredToggles] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  
  // History Modal State
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');

  // Form instance for inputs in table
  const [form] = Form.useForm();
  // We keep a state of edited values to determine if save should be active
  const [modifiedValues, setModifiedValues] = useState({});

  const fetchToggles = async () => {
    setLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.authUrl}/api/v1/admin/security-toggles`);
      const data = res?.data || res;
      if (data && Array.isArray(data)) {
        setToggles(data);
        setFilteredToggles(data);
        
        // Populate form initial values
        const formVals = {};
        data.forEach(item => {
          const key = item.key || item.configKey;
          const val = item.value !== undefined ? item.value : item.configValue;
          const type = item.valueType || 'STRING';
          formVals[key] = type === 'BOOLEAN' ? (val === 'true' || val === true) : val;
        });
        form.setFieldsValue(formVals);
        setModifiedValues({});
      } else {
        setToggles([]);
        setFilteredToggles([]);
      }
    } catch (err) {
      NotificationManager.error(err.message || 'Failed to load security toggles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToggles();
  }, []);

  // Filter list on search text change
  useEffect(() => {
    if (!searchText) {
      setFilteredToggles(toggles);
    } else {
      const lower = searchText.toLowerCase();
      const filtered = toggles.filter(item => {
        const key = (item.key || item.configKey || '').toLowerCase();
        const desc = (item.description || '').toLowerCase();
        return key.includes(lower) || desc.includes(lower);
      });
      setFilteredToggles(filtered);
    }
  }, [searchText, toggles]);

  const validateValue = (val, type) => {
    if (val === undefined || val === null || val === '') {
      return { valid: false, msg: "Value cannot be empty" };
    }
    const strVal = String(val).trim();

    if (type === "BOOLEAN") {
      if (strVal !== "true" && strVal !== "false" && strVal !== "1" && strVal !== "0") {
        return { valid: false, msg: "Must be 'true' or 'false'" };
      }
    } else if (type === "INTEGER") {
      if (!/^-?\d+$/.test(strVal)) {
        return { valid: false, msg: "Must be a valid integer" };
      }
    } else if (type === "DECIMAL") {
      if (!/^-?\d+(\.\d+)?$/.test(strVal)) {
        return { valid: false, msg: "Must be a valid decimal number" };
      }
    }
    return { valid: true };
  };

  const handleSave = async (record) => {
    const key = record.key || record.configKey;
    const type = record.valueType || 'STRING';
    let currentValue = form.getFieldValue(key);

    // Convert boolean if needed
    if (type === 'BOOLEAN' && typeof currentValue === 'boolean') {
      currentValue = currentValue ? 'true' : 'false';
    }

    if (record.sensitive && String(currentValue).trim() === '***') {
      NotificationManager.error("Please enter a new value. The current sensitive value is masked.");
      return;
    }

    const valCheck = validateValue(currentValue, type);
    if (!valCheck.valid) {
      NotificationManager.error(`${key}: ${valCheck.msg}`);
      return;
    }

    setSavingKey(key);
    try {
      const payload = {
        value: String(currentValue).trim()
      };

      const res = await httpPut(`${customBaseUrl.authUrl}/api/v1/admin/security-toggles/${key}`, payload);
      if (res && (res.success || res.status === 'SUCCESS' || res.status === true)) {
        Swal.fire({
          title: "Security Toggle Saved",
          html: `Security parameter <strong>${key}</strong> updated successfully.<br/><br/><span style="color: #ff4d4f; font-weight: bold;">Note: Changes take effect across service pods immediately.</span>`,
          icon: "success",
          confirmButtonColor: "#ff6700"
        });
        
        // Remove from modified values tracked
        const nextModified = { ...modifiedValues };
        delete nextModified[key];
        setModifiedValues(nextModified);
        
        fetchToggles();
      } else {
        NotificationManager.error(res?.message || "Failed to update security toggle.");
      }
    } catch (err) {
      NotificationManager.error(err.message || "An error occurred while saving.");
    } finally {
      setSavingKey(null);
    }
  };

  const handleFetchHistory = async (key) => {
    setSelectedKey(key);
    setHistoryVisible(true);
    setHistoryLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.authUrl}/api/v1/admin/security-toggles/${key}/history`);
      const data = res?.data || res;
      if (data && Array.isArray(data)) {
        setHistoryData(data);
      } else {
        setHistoryData([]);
      }
    } catch (err) {
      NotificationManager.error(err.message || "Failed to retrieve change history.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const columns = [
    {
      title: 'Security Toggle Key',
      dataIndex: 'key',
      key: 'key',
      render: (text, record) => {
        const key = record.key || record.configKey;
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600, color: '#262626' }}>
              {key}
              <Tooltip title={record.description || 'No description provided'}>
                <InfoCircleOutlined style={{ marginLeft: 8, color: '#bfbfbf', cursor: 'pointer' }} />
              </Tooltip>
            </span>
            {record.sensitive && (
              <span style={{ marginTop: 4 }}>
                <Tag color="red" size="small" style={{ fontSize: '10px' }}>Sensitive</Tag>
              </span>
            )}
          </div>
        );
      }
    },
    {
      title: 'Value',
      key: 'value',
      width: 250,
      render: (text, record) => {
        const key = record.key || record.configKey;
        const type = record.valueType || 'STRING';

        return (
          <Form.Item
            name={key}
            valuePropName={type === 'BOOLEAN' ? 'checked' : 'value'}
            style={{ margin: 0 }}
          >
            {type === 'BOOLEAN' ? (
              <Switch 
                checkedChildren="ON" 
                unCheckedChildren="OFF" 
                onChange={(checked) => {
                  const originalVal = record.value !== undefined ? record.value : record.configValue;
                  const isChanged = String(checked) !== String(originalVal);
                  setModifiedValues(prev => ({
                    ...prev,
                    [key]: isChanged
                  }));
                }}
              />
            ) : record.sensitive ? (
              <Input.Password 
                placeholder="Sensitive value masked"
                onChange={(e) => {
                  const val = e.target.value;
                  const originalVal = record.value !== undefined ? record.value : record.configValue;
                  const isChanged = String(val) !== String(originalVal);
                  setModifiedValues(prev => ({
                    ...prev,
                    [key]: isChanged
                  }));
                }}
              />
            ) : (
              <Input 
                placeholder="Enter value" 
                onChange={(e) => {
                  const val = e.target.value;
                  const originalVal = record.value !== undefined ? record.value : record.configValue;
                  const isChanged = String(val) !== String(originalVal);
                  setModifiedValues(prev => ({
                    ...prev,
                    [key]: isChanged
                  }));
                }}
              />
            )}
          </Form.Item>
        );
      }
    },
    {
      title: 'Last Modified',
      key: 'lastModified',
      render: (text, record) => {
        const updatedAt = record.updatedAt || record.updatedDate;
        const modifiedBy = record.updatedBy || record.updatedByUserId;
        return (
          <div style={{ fontSize: '12px' }}>
            {modifiedBy ? (
              <div><strong>By:</strong> {modifiedBy}</div>
            ) : (
              <div style={{ color: '#bfbfbf' }}>System Default</div>
            )}
            {updatedAt && (
              <div style={{ color: '#8c8c8c', fontSize: '11px' }}>
                {moment(updatedAt).format('YYYY-MM-DD HH:mm:ss')}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      render: (text, record) => {
        const key = record.key || record.configKey;
        const isChanged = modifiedValues[key];

        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SaveOutlined />}
              onClick={() => handleSave(record)}
              loading={savingKey === key}
              disabled={!isChanged}
              style={{
                background: isChanged ? '#ff6700' : '#f5f5f5',
                borderColor: isChanged ? '#ff6700' : '#d9d9d9',
                color: isChanged ? '#fff' : '#rgba(0, 0, 0, 0.25)'
              }}
            >
              Save
            </Button>
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => handleFetchHistory(key)}
            >
              History
            </Button>
          </Space>
        );
      }
    }
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '12px 16px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8 }}>
        <span style={{ color: '#595959', fontSize: '13px' }}>
          Security feature toggles provide runtime control over authentication, rate limits, and path protections.
        </span>
        <Button size="small" type="default" icon={<ReloadOutlined />} onClick={fetchToggles} loading={loading}>
          Reload Toggles
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by toggle key or description..."
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ maxWidth: 400 }}
        />
      </div>

      <Spin spinning={loading} tip="Fetching security toggles...">
        <Form form={form} component={false}>
          <Table
            dataSource={filteredToggles}
            columns={columns}
            rowKey={(record) => record.key || record.configKey}
            pagination={{ pageSize: 15 }}
            bordered
            size="middle"
          />
        </Form>
      </Spin>

      {/* Audit History Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryOutlined style={{ color: '#ff6700' }} />
            <span>Audit History: <Text code>{selectedKey}</Text></span>
          </div>
        }
        visible={historyVisible}
        onCancel={() => setHistoryVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
        destroyOnClose
      >
        <Spin spinning={historyLoading} tip="Loading history timeline...">
          <div style={{ maxHeight: 400, overflowY: 'auto', padding: '16px 8px' }}>
            {historyData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#8c8c8c' }}>
                No audit logs found for this security toggle.
              </div>
            ) : (
              <Timeline mode="left">
                {historyData.map((item, index) => (
                  <Timeline.Item 
                    key={index}
                    label={moment(item.createdAt || item.updatedAt || item.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                    color={index === 0 ? 'green' : 'blue'}
                  >
                    <div style={{ marginBottom: 4 }}>
                      <strong>Value changed to:</strong> <Text code>{String(item.newValue !== undefined ? item.newValue : (item.value !== undefined ? item.value : item.configValue))}</Text>
                    </div>
                    {item.oldValue !== undefined && item.oldValue !== null && (
                      <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: 4 }}>
                        Previous Value: <Text delete>{String(item.oldValue)}</Text>
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#595959' }}>
                      <strong>Modified By:</strong> {item.changedByUserId || item.updatedByUserId || item.updatedBy || item.performedBy || 'System/Admin'}
                    </div>
                    {item.source && (
                      <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: 4 }}>
                        Source: <Tag size="small">{item.source}</Tag>
                      </div>
                    )}
                    {item.description && (
                      <div style={{ fontSize: '11px', color: '#bfbfbf', marginTop: 4, fontStyle: 'italic' }}>
                        Reason: {item.description}
                      </div>
                    )}
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </div>
        </Spin>
      </Modal>
    </div>
  );
};

export default SecurityToggleSettings;
